import { useMemo, useCallback } from 'react';
import { useAssetActions } from 'asset-actions-lib/context/AssetActionsContext';
import { ACTION_IDS } from 'asset-actions-lib/constants/actionIds';
import FormDeleteLoadingModal from 'asset-actions-lib-forms/delete/FormDeleteLoadingModal';
import FormDeleteCosAlertModal from 'asset-actions-lib-forms/delete/FormDeleteCosAlertModal';
import FormDeletePromptModal from 'asset-actions-lib-forms/delete/FormDeletePromptModal';
import { useFetchPageCounts } from 'asset-actions-lib-forms/delete/hooks/useFetchPageCounts';
import { useFetchPagesForForm } from 'asset-actions-lib-forms/delete/hooks/useFetchPagesForForm';
import { useDeleteForm } from 'asset-actions-lib-forms/delete/hooks/useDeleteForm';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MODAL_WIDTH = 540;
const LANDINGPAGES_ACCESS = 'landingpages-access';
const BLOG_ACCESS = 'blog-access';
const SITEPAGES_ACCESS = 'sitepages-access';
const modalProps = {
  width: MODAL_WIDTH
};
export default function FormDeleteModal({
  onClose,
  onConfirm,
  scopes
}) {
  const {
    crmObject,
    onActionError
  } = useAssetActions();
  const formName = useMemo(() => getPropertyValue(crmObject, 'hs_name'), [crmObject]);
  const formType = useMemo(() => getPropertyValue(crmObject, 'hs_form_type'), [crmObject]);
  const guid = useMemo(() => getPropertyValue(crmObject, 'hs_form_id'), [crmObject]);
  const embedVersion = useMemo(() => getPropertyValue(crmObject, 'hs_embed_version'), [crmObject]);
  const hasHubSpotPagesAccess = useMemo(() => scopes.includes(LANDINGPAGES_ACCESS) || scopes.includes(BLOG_ACCESS) || scopes.includes(SITEPAGES_ACCESS), [scopes]);
  const {
    data: pageCounts,
    loading: pageCountsLoading,
    error: pageCountsError
  } = useFetchPageCounts({
    guids: [guid],
    skip: !hasHubSpotPagesAccess
  });
  const {
    data: pagesForForm,
    loading: pagesForFormLoading,
    error: pagesForFormError
  } = useFetchPagesForForm({
    guid,
    skip: !pageCounts || !pageCounts[guid]
  });
  const {
    deleteForm,
    loading: deleteLoading,
    error: deleteError
  } = useDeleteForm({
    guid,
    formType,
    embedVersion
  });
  const handleDelete = useCallback(() => {
    deleteForm().then(() => {
      onConfirm(ACTION_IDS.DELETE, crmObject);
      onClose();
      if (deleteError) {
        if (onActionError) {
          onActionError({
            actionId: ACTION_IDS.DELETE,
            data: deleteError
          });
        }
      }
    }).catch(() => {
      onClose();
      if (onActionError) {
        onActionError({
          actionId: ACTION_IDS.DELETE,
          data: deleteError
        });
      }
    });
  }, [crmObject, deleteError, deleteForm, onActionError, onClose, onConfirm]);
  const showCosLoadingModal = pageCountsLoading || pagesForFormLoading;
  const showCosAlertModal = hasHubSpotPagesAccess && formType !== 'CAPTURED' && pagesForForm && pagesForForm.length > 0 && pageCounts && !!pageCounts[guid];
  const showDeleteModal = !showCosLoadingModal && !showCosAlertModal && !pageCountsError && !pagesForFormError;
  const modalComponentProps = useMemo(() => {
    if (showDeleteModal) {
      return Object.assign({
        'data-test-id': 'form-delete-prompt-modal',
        use: 'danger'
      }, modalProps);
    }
    if (showCosAlertModal) {
      return Object.assign({
        'data-test-id': 'form-delete-cos-alert-modal',
        onEsc: onClose
      }, modalProps);
    }
    return Object.assign({
      'data-test-id': 'form-delete-loading-modal',
      onEsc: onClose
    }, modalProps);
  }, [showDeleteModal, showCosAlertModal, onClose]);

  // close modal if cos requests fail
  if (!showDeleteModal && !showCosLoadingModal && !showCosAlertModal) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Modal, Object.assign({}, modalComponentProps, {
    children: [showCosLoadingModal && /*#__PURE__*/_jsx(FormDeleteLoadingModal, {
      handleClose: onClose,
      formType: formType
    }), showCosAlertModal && /*#__PURE__*/_jsx(FormDeleteCosAlertModal, {
      handleClose: onClose,
      formType: formType,
      formName: formName,
      pagesForForm: pagesForForm || []
    }), showDeleteModal && /*#__PURE__*/_jsx(FormDeletePromptModal, {
      handleClose: onClose,
      handleDelete: handleDelete,
      formType: formType,
      formName: formName,
      hasHubSpotPagesAccess: hasHubSpotPagesAccess,
      isDeleteLoading: deleteLoading,
      guid: guid
    })]
  }));
}